import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import {
  Skeleton,
  TableSortLabel,
  Typography,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  Box,
  Button,
} from '@mui/material';
import CustomPagination from '../../../../components/forms/Pagination';
import { EmptyTable } from '../../../../components/shared/EmptyTable';
import { Catalogue, client } from '../../../../app/services/api/orderManagementClient';
import { UserContext } from '../../../../components/shared/useUser';
import useLogError from '../../../../hooks/useLogError';

interface CataloguesTableProps {
  list: Catalogue[];
  isLoading: boolean;
  searchTerm: string;
  clearAll: () => void;
  page: number;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  order: 'asc' | 'desc';
  setOrder: React.Dispatch<React.SetStateAction<'asc' | 'desc'>>;
  orderBy: string;
  setOrderBy: React.Dispatch<React.SetStateAction<string>>;
  totalPages: number;
}

type SortLabel = 'name'

const CataloguesTable = ({ list, isLoading, searchTerm, clearAll, page, setPage, order, setOrder, orderBy, setOrderBy, totalPages }: CataloguesTableProps) => {
  
  const { t } = useTranslation('catalogues');
  const theme = useTheme();
  const { hasPermissionTo, selectedStore } = useContext(UserContext);
  const navigate = useNavigate();
  const { logError } = useLogError(); const [retailersDialogOpen, setRetailersDialogOpen] = useState(false);
  const [retailers, setRetailers] = useState<string[]>([]);
  const [loadingRetailers, setLoadingRetailers] = useState(false);

  const handleSort = (property: SortLabel) => {
    const isAsc = orderBy === property && order === 'asc';
    setPage(0);
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };
  
  const handleViewRetailers = async (catalogueId: number) => {
    setLoadingRetailers(true);
    setRetailersDialogOpen(true);

    try {
      const response = await client.getCatalogueRetailers(catalogueId.toString(), selectedStore?.storeNumber || '');
      setRetailers(response.data);
    } catch (error) {
      logError(error);
    } finally {
      setLoadingRetailers(false);
    }
  };

  const handleCloseDialog = () => {
    setRetailersDialogOpen(false);
  };

  const handleDialogExited = () => {
    setRetailers([]);
    setLoadingRetailers(false);
  };

  if (!isLoading && list.length === 0) {
    return <EmptyTable
      text={t('title')}
      searchTerm={searchTerm}
      onClear={clearAll}
      onAdd={() => { }}
      addItemText={t('newCatalogue')}
      onAddIsCreating={false} />;
  }

  return (
    <>
      {isLoading
        ?
        <Skeleton sx={{ bgcolor: theme.palette.custom.gray[100] }}
          variant='rectangular'
          height={445.89}
        />
        :
        <TableContainer sx={{ borderRadius: '12px' }}
          component='div'>
          <Table size="small">
            <TableHead sx={{ backgroundColor: theme.palette.custom.gray[200] }}>
              <TableRow>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'name'}
                    direction={order}
                    onClick={() => handleSort('name')}
                  >
                    {t('name')}
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  {t('orderType')}
                </TableCell>
                <TableCell>
                  {t('market')}
                </TableCell>
                <TableCell>
                  {t('retailers')}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {list.map((row, index) => (
                <TableRow
                  key={`${index}${row.name}`}
                  sx={{ cursor: 'pointer' }}
                >                <TableCell
                    onClick={hasPermissionTo(['CataloguesDetailView']) ? () => navigate(`/ofd/catalogueproducts/${row.id}`) : undefined}
                  >
                    <Typography variant='textXS'
                      fontWeight={600}
                      color={theme.palette.primary[500]}
                      sx={hasPermissionTo(['CataloguesDetailView']) ? { textDecoration: 'none', '&:hover': { textDecoration: 'underline' } } : {}}
                    >
                      {row.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant='textXS'>
                      {row.orderType || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant='textXS'>
                      {row.market || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant='textXS'
                      component="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleViewRetailers(row.id);
                      }}
                      sx={{
                        background: 'none',
                        border: 'none',
                        color: theme.palette.primary[500],
                        cursor: 'pointer',
                        textDecoration: 'underline',
                        padding: 0,
                        '&:hover': {
                          opacity: 0.8,
                        },
                      }}
                    >
                      {row.retailerCount}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
              {/* Add empty rows to maintain height */}            {Array.from({ length: Math.max(0, 10 - (list.length ?? 0)) }).map((_, index) => (
                <TableRow key={`empty-row-${index}`}
                  sx={{ height: '40px', backgroundColor: theme.palette.common.white }}>
                  <TableCell colSpan={4}
                    sx={{
                      backgroundColor: theme.palette.common.white,
                      borderColor: index === Math.max(0, 10 - (list.length ?? 0)) - 1 ? theme.palette.custom.gray[100] : theme.palette.common.white,
                    }} />
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>}
      <CustomPagination
        page={page}
        setPage={setPage}
        maxPages={totalPages}
        breakpointForChangeDisplay={120000}
        disabled={isLoading}
      />
      <Dialog
        open={retailersDialogOpen}
        onClose={handleCloseDialog}
        TransitionProps={{
          onExited: handleDialogExited,
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {t('retailersForCatalogue')}
        </DialogTitle>
        <DialogContent>
          {loadingRetailers ? (
            <Box display="flex"
              justifyContent="center"
              p={2}>
              <CircularProgress />
            </Box>
          ) : (
            <List>
              {retailers.map((retailer, index) => (
                <ListItem key={index}>
                  <ListItemText primary={retailer} />
                </ListItem>
              ))}
              {retailers.length === 0 && (
                <Typography variant="textSM"
                  color="textSecondary"
                  align="center">
                  {t('noRetailersFound')}
                </Typography>
              )}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>
            {t('close')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CataloguesTable;
